import { useState } from 'react';

import { getSessionHeaders } from '../../lib/serverSession';
interface ManualRecordCreatorProps {
  table: string;
  fields: string[];
  onClose: () => void;
  onCreate: (newRecord: any) => void;
}

export function ManualRecordCreator({
  table,
  fields,
  onClose,
  onCreate
}: ManualRecordCreatorProps) {
  const [newRecord, setNewRecord] = useState<Record<string, any>>({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFieldChange = (field: string, value: any) => {
    setNewRecord({
      ...newRecord,
      [field]: value
    });
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError('');
      setSuccess('');

      // Validate required fields (non-empty)
      const requiredFields = fields.filter(f => !f.includes('id') && !f.includes('created_at') && !f.includes('updated_at'));
      const missingFields = requiredFields.filter(f => newRecord[f] === undefined || newRecord[f] === null || newRecord[f] === '');

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // Validate that we have a valid admin user id for audit logging
      const adminUserId = typeof (window as any).currentUserId === 'number' ? (window as any).currentUserId : null;
      if (!adminUserId) {
        throw new Error('Current admin user is not available. Please re-open the admin dashboard and try again.');
      }

      const response = await fetch('/api/admin/manual-record-editor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          action: 'CREATE',
          table,
          record: newRecord,
          userId: adminUserId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create record');
      }

      const data = await response.json();
      setSuccess('✅ Record created successfully');
      onCreate(data.data);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(`❌ Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const getFieldType = (field: string): string => {
    // Check for status field first (before date check)
    if (field === 'status') return 'select';

    // Check value type for more accurate detection
    if (field.includes('amount') || field.includes('commission_rate') || field.includes('price') || field.includes('shares')) return 'number';

    // Check for datetime fields - be specific to avoid matching 'commission_rate'
    if (field.endsWith('_at') || field.endsWith('_date') || field === 'created_at' || field === 'updated_at') return 'datetime-local';

    // Check for ID fields that are numeric
    if (field.includes('id') && !field.includes('user') && !field.includes('referrer') && !field.includes('referred')) return 'number';

    return 'text';
  };

  const editableFields = fields.filter(
    f => !f.includes('id') && !f.includes('created_at') && !f.includes('updated_at')
  );

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#1F2937',
        border: '1px solid #374151',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: '24px'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#FFFFFF', margin: 0 }}>
            ➕ Add New {table} Record
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: 0
            }}
          >
            ✕
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            backgroundColor: '#7F1D1D',
            color: '#FCA5A5',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            backgroundColor: '#1F3A1F',
            color: '#86EFAC',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {success}
          </div>
        )}

        {/* Form Fields */}
        <div style={{ marginBottom: '24px' }}>
          {editableFields.map((field) => (
            <div key={field} style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '6px',
                textTransform: 'uppercase'
              }}>
                {field.replace(/_/g, ' ')} *
              </label>
              {getFieldType(field) === 'select' ? (
                <select
                  value={newRecord[field] || ''}
                  onChange={(e) => handleFieldChange(field, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select...</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              ) : getFieldType(field) === 'number' ? (
                <input
                  type="number"
                  step="0.01"
                  placeholder={`Enter ${field}`}
                  value={newRecord[field] ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      handleFieldChange(field, '');
                      return;
                    }
                    const parsed = Number.parseFloat(raw);
                    handleFieldChange(field, Number.isFinite(parsed) ? parsed : '');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              ) : getFieldType(field) === 'datetime-local' ? (
                <input
                  type="datetime-local"
                  value={newRecord[field] || ''}
                  onChange={(e) => handleFieldChange(field, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              ) : (
                <input
                  type="text"
                  placeholder={`Enter ${field}`}
                  value={newRecord[field] || ''}
                  onChange={(e) => handleFieldChange(field, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={creating}
            style={{
              padding: '10px 20px',
              backgroundColor: '#374151',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: creating ? 0.5 : 1
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleCreate}
            disabled={creating}
            style={{
              padding: '10px 20px',
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: creating ? 0.5 : 1
            }}
          >
            {creating ? '⏳ Creating...' : '➕ Create Record'}
          </button>
        </div>
      </div>
    </div>
  );
}

